import { c as _c } from "react/compiler-runtime";
import { cx } from 'class-variance-authority';
import type React from 'react';
import ChatWidgetFullscreenSlot from '@/components/agent-chat/components/chat-widget/chat-widget-fullscreen-slot';
import ChatWidgetSlot from '@/components/agent-chat/components/chat-widget/chat-widget-slot';
import useChatWidgetFullscreenActive from '@/components/agent-chat/hooks/use-chat-widget-fullscreen-active';
import useChatWidgetSlotWidth from '@/components/agent-chat/hooks/use-chat-widget-slot-width';
import useSideNavCollapsed from '@/components/layout/hooks/use-side-nav-collapsed';
import { MAIN_PAGE_ID } from '@/config/constants';
import useMobileViewport from '@/hooks/use-mobile-viewport';
import useAssumeBanner from '../assume-banner/hooks/use-assume-banner';
import ResponsiveNav from '../responsive-nav';
import { Banners } from './banners';
type DefaultLayoutProps = {
  children: React.ReactNode;
};
const DefaultLayout = t0 => {
  const $ = _c(36);
  const {
    children
  } = t0;
  const {
    isCollapsed,
    setCollapsed
  } = useSideNavCollapsed();
  const {
    isAssumedSessionEditMode
  } = useAssumeBanner();
  const mobileViewport = useMobileViewport();
  const {
    rowRef,
    writeWidthVar,
    setResizing
  } = useChatWidgetSlotWidth();
  const fullscreenActive = useChatWidgetFullscreenActive();
  const t1 = mobileViewport?.keyboardOpen || undefined;
  let t2;
  if ($[0] !== mobileViewport) {
    t2 = mobileViewport && {
      height: mobileViewport.height,
      top: mobileViewport.top
    };
    $[0] = mobileViewport;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== isCollapsed) {
    t3 = cx("group/app grid grid-cols-1 grid-rows-[auto_minmax(0,1fr)] md:grid-rows-1 md:grid-cols-[var(--side-nav-width)_1fr] h-dvh pt-[env(safe-area-inset-top)] md:pt-0 overflow-hidden relative max-md:fixed max-md:inset-x-0 max-md:top-0 bg-canvas min-h-0 transition-[grid-template-columns] duration-200", {
      "md:grid-cols-[var(--side-nav-width-collapsed)_1fr]": isCollapsed
    });
    $[2] = isCollapsed;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== isCollapsed || $[5] !== setCollapsed) {
    t4 = <ResponsiveNav isCollapsed={isCollapsed} onSideNavCollapse={setCollapsed} />;
    $[4] = isCollapsed;
    $[5] = setCollapsed;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = cx("flex flex-col min-w-0 overflow-hidden md:py-2 md:pr-2 w-[calc(100%-var(--chat-widget-width,0px))]", "group-data-animate-width/chat:transition-[width] group-data-animate-width/chat:duration-200", "group-data-resizing/chat:transition-none");
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = !isAssumedSessionEditMode;
  let t7;
  if ($[8] !== isAssumedSessionEditMode || $[9] !== t6) {
    t7 = cx("md:relative bg-primary md:rounded-lg overflow-hidden flex flex-col flex-1 w-full", {
      "border-error border-[1.5px]": isAssumedSessionEditMode,
      "md:border md:border-tertiary": t6
    });
    $[8] = isAssumedSessionEditMode;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Banners />;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const t9 = fullscreenActive ? "overflow-hidden" : "overflow-x-hidden overflow-y-auto";
  let t10;
  if ($[12] !== t9) {
    t10 = cx("relative flex-1 min-w-0 p-4 pb-24 md:p-7", t9);
    $[12] = t9;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== children) {
    t11 = <div className="max-w-(--main-content-max-width) mx-auto w-full">{children}</div>;
    $[14] = children;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== t10 || $[17] !== t11) {
    t12 = <main id={MAIN_PAGE_ID} className={t10}>{t11}</main>;
    $[16] = t10;
    $[17] = t11;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <ChatWidgetFullscreenSlot />;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== t12 || $[21] !== t7) {
    t14 = <div className={t5}><div className={t7}>{t8}{t12}{t13}</div></div>;
    $[20] = t12;
    $[21] = t7;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== setResizing || $[24] !== writeWidthVar) {
    t15 = <ChatWidgetSlot onResizeWidth={writeWidthVar} onResizingChange={setResizing} />;
    $[23] = setResizing;
    $[24] = writeWidthVar;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== rowRef || $[27] !== t14 || $[28] !== t15) {
    t16 = <div ref={rowRef} className="col-start-1 row-start-2 md:col-start-auto md:row-start-auto group/chat flex min-w-0 min-h-full overflow-hidden md:relative">{t14}{t15}</div>;
    $[26] = rowRef;
    $[27] = t14;
    $[28] = t15;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== t1 || $[31] !== t16 || $[32] !== t2 || $[33] !== t3 || $[34] !== t4) {
    t17 = <div data-testid="private-default-layout" data-keyboard-open={t1} style={t2} className={t3}>{t4}{t16}</div>;
    $[30] = t1;
    $[31] = t16;
    $[32] = t2;
    $[33] = t3;
    $[34] = t4;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  return t17;
};
export default DefaultLayout;